import type { PaginatedResponse } from '@/shared/types/api.types';
export interface IngredientCategory {
  id: number;
  version: number;
  name: string;
  nameEn?: string;
  nameEs?: string;
  nameFr?: string;
  positionIndex: number;
}
import type { MenuIngredientView } from '../types/menu.types';
import { API_TAGS } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';
import {
  buildBranchListQueryString,
  normalizePro20PageResponse,
} from '@/shared/api/normalize-pro20-page';
import { normalizePro20Response } from '@/shared/api/pro20-transform';
import type { Pro20ApiResponse } from '@/shared/types/api-response.types';

import type {
  CreateMenuPayload,
  CreateRecipePayload,
  CreateSectionPayload,
  DeleteMenuParams,
  DeleteRecipeParams,
  DeleteSectionParams,
  Menu,
  MenuCodeParams,
  MenuListParams,
  MenuListResult,
  MenuSection,
  MenuRecipe,
  MenuUploadParams,
  MenuUploadResponse,
  MoveMenuRecipePayload,
  MoveMenuSectionPayload,
  RecipeUploadParams,
  SortSectionRecipesParams,
  TranslateTextParams,
  TranslateTextResult,
  UpdateMenuPayload,
  UpdateRecipePayload,
  UpdateSectionPayload,
} from '../types/menu.types';
import { menuUpdateRequest, recipeRequest } from '../utils/menu-api-contract';
import {
  normalizeMenu,
  normalizeSection,
  normalizeRecipe,
} from '../utils/normalize-menu';

function buildMenuInvalidationTags(branchCode?: string, menuCode?: string) {
  return [
    { type: API_TAGS.Menu, id: 'LIST' as const },
    ...(branchCode ? [{ type: API_TAGS.Menu, id: branchCode }] : []),
    ...(menuCode ? [{ type: API_TAGS.Menu, id: menuCode }] : []),
  ];
}

function readTranslatedText(payload: unknown): string {
  if (typeof payload === 'string') {
    return payload;
  }

  if (!payload || typeof payload !== 'object') {
    return '';
  }

  const item = payload as Record<string, unknown>;
  const candidates = [
    item.translatedText,
    item.translation,
    item.text,
    item.result,
    item.translated,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate;
    }
  }

  return '';
}

export const menuApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getIngredientCategories: builder.query<
      IngredientCategory[],
      { branchCode: string }
    >({
      query: ({ branchCode }) => ({
        url: '/menu/ingredients/categories',
        params: { branchCode },
      }),
      transformResponse: (response: unknown) =>
        normalizePro20Response(response) as IngredientCategory[],
      providesTags: [{ type: API_TAGS.Menu, id: 'INGREDIENTS' }],
    }),
    saveIngredientCatalog: builder.mutation<
      unknown,
      {
        branchCode: string;
        category: boolean;
        id?: number;
        remove?: boolean;
        data?: unknown;
      }
    >({
      query: ({ branchCode, category, id, remove, data }) => ({
        url:
          '/menu/ingredients' +
          (category ? '/categories' : '') +
          (id === undefined ? '' : '/' + String(id)),
        params: { branchCode },
        method: remove ? 'DELETE' : id === undefined ? 'POST' : 'PUT',
        body: data,
      }),
      invalidatesTags: [API_TAGS.Menu],
    }),
    getMenuIngredientPage: builder.query<
      PaginatedResponse<MenuIngredientView>,
      {
        branchCode: string;
        page: number;
        size: number;
        search?: string;
        categoryId?: number;
      }
    >({
      query: (params) => ({ url: '/menu/ingredients', params }),
      transformResponse: (response: unknown, _meta, arg) =>
        normalizePro20PageResponse(
          normalizePro20Response(response),
          (item) => item as MenuIngredientView,
          arg.size,
        ),
      providesTags: [{ type: API_TAGS.Menu, id: 'INGREDIENTS' }],
    }),
    getMenuIngredients: builder.query<
      MenuIngredientView[],
      { branchCode: string; ids?: number[] }
    >({
      query: ({ branchCode, ids }) => ({
        url: '/menu/ingredients',
        params: { branchCode, ...(ids?.length ? { ids: ids.join(',') } : {}) },
      }),
      transformResponse: (response: unknown) => {
        const raw = normalizePro20Response(response);
        return Array.isArray(raw) ? (raw as MenuIngredientView[]) : [];
      },
      providesTags: [{ type: API_TAGS.Menu, id: 'INGREDIENTS' }],
    }),
    getMenusByBranch: builder.query<MenuListResult, MenuListParams>({
      query: ({ branchCode, page, size, sortBy, sortDirection, search }) => ({
        url: `/menu/getbybranchcode/${branchCode}${buildBranchListQueryString({
          page,
          size,
          sortBy,
          sortDirection,
          search,
        })}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown, _meta, arg) =>
        normalizePro20PageResponse(
          normalizePro20Response(response),
          normalizeMenu,
          arg.size ?? 20,
        ),
      providesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Menu, id: branchCode },
        { type: API_TAGS.Menu, id: 'LIST' },
      ],
    }),

    getMenuByCode: builder.query<Menu | null, MenuCodeParams>({
      query: ({ menuCode, search }) => ({
        url: `/menu/${menuCode}`,
        method: 'GET',
        params: search?.trim() ? { search: search.trim() } : undefined,
      }),
      transformResponse: (response: unknown) => {
        const raw = normalizePro20Response(response);
        if (Array.isArray(raw) && raw.length === 0) return null;
        const normalized = normalizeMenu(raw);
        if (!normalized) {
          throw new Error('Invalid menu response');
        }
        return normalized;
      },
      providesTags: (result) =>
        result
          ? [
              { type: API_TAGS.Menu, id: result.menuCode },
              { type: API_TAGS.Menu, id: result.branchCode },
            ]
          : [],
    }),

    createMenu: builder.mutation<Menu, CreateMenuPayload>({
      query: (body) => ({
        url: '/menu/create',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeMenu(normalizePro20Response(response));
        if (!normalized) {
          throw new Error('Invalid create menu response');
        }
        return normalized;
      },
      invalidatesTags: (_result, _error, { branchCode }) =>
        buildMenuInvalidationTags(branchCode),
    }),

    updateMenu: builder.mutation<Menu, UpdateMenuPayload>({
      query: (body) => ({
        url: '/menu/update',
        method: 'PUT',
        body: menuUpdateRequest(body),
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeMenu(normalizePro20Response(response));
        if (!normalized) {
          throw new Error('Invalid update menu response');
        }
        return normalized;
      },
      invalidatesTags: (result) =>
        buildMenuInvalidationTags(result?.branchCode, result?.menuCode),
    }),

    deleteMenu: builder.mutation<null, DeleteMenuParams>({
      query: ({ menuCode }) => ({
        url: `/menu/delete/${menuCode}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { branchCode, menuCode }) =>
        buildMenuInvalidationTags(branchCode, menuCode),
    }),

    duplicateMenu: builder.mutation<Menu, DeleteMenuParams>({
      query: ({ menuCode }) => ({
        url: `/menu/duplicate/${menuCode}`,
        method: 'PUT',
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeMenu(normalizePro20Response(response));
        if (!normalized) {
          throw new Error('Invalid duplicate menu response');
        }
        return normalized;
      },
      invalidatesTags: (_result, _error, { branchCode, menuCode }) =>
        buildMenuInvalidationTags(branchCode, menuCode),
    }),

    createMenuSection: builder.mutation<Menu, CreateSectionPayload>({
      query: ({ menuCode, name, description }) => ({
        url: `/menu/create/section/${menuCode}`,
        method: 'PUT',
        body: {
          name,
          description: description ?? '',
        },
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeMenu(normalizePro20Response(response));
        if (!normalized) {
          throw new Error('Invalid create section response');
        }
        return normalized;
      },
      invalidatesTags: (_result, _error, { branchCode, menuCode }) =>
        buildMenuInvalidationTags(branchCode, menuCode),
    }),

    updateMenuSection: builder.mutation<MenuSection, UpdateSectionPayload>({
      query: ({ sectionCode, body }) => ({
        url: `/menu/update/section/${sectionCode}`,
        method: 'PUT',
        body: { ...body, isVisible: body.visible },
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeSection(normalizePro20Response(response));
        if (!normalized) {
          throw new Error('Invalid update section response');
        }
        return normalized;
      },
      invalidatesTags: (_result, _error, { branchCode, menuCode }) =>
        buildMenuInvalidationTags(branchCode, menuCode),
    }),

    deleteMenuSection: builder.mutation<null, DeleteSectionParams>({
      query: ({ sectionCode }) => ({
        url: `/menu/delete/section/${sectionCode}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { branchCode, menuCode }) =>
        buildMenuInvalidationTags(branchCode, menuCode),
    }),

    moveMenuSection: builder.mutation<Menu | null, MoveMenuSectionPayload>({
      query: ({ menuCode, sectionCode, newIndex }) => ({
        url: '/menu/move/section',
        method: 'PUT',
        body: { menuCode, sectionCode, newIndex },
      }),
      transformResponse: (response: unknown) =>
        normalizeMenu(normalizePro20Response(response)),
      invalidatesTags: (_result, _error, { branchCode, menuCode }) =>
        buildMenuInvalidationTags(branchCode, menuCode),
    }),

    moveMenuRecipe: builder.mutation<Menu | null, MoveMenuRecipePayload>({
      query: ({
        sourceSectionCode,
        targetSectionCode,
        recipeCode,
        newIndex,
      }) => ({
        url: '/menu/move/recipe',
        method: 'PUT',
        body: { recipeCode, sourceSectionCode, targetSectionCode, newIndex },
      }),
      transformResponse: (response: unknown) =>
        normalizeMenu(normalizePro20Response(response)),
      invalidatesTags: (_result, _error, { branchCode, menuCode }) =>
        buildMenuInvalidationTags(branchCode, menuCode),
    }),

    sortSectionRecipes: builder.mutation<MenuSection, SortSectionRecipesParams>(
      {
        query: ({ sectionCode }) => ({
          url: `/menu/sortalphabetically/${sectionCode}`,
          method: 'PUT',
        }),
        transformResponse: (response: unknown) => {
          const normalized = normalizeSection(normalizePro20Response(response));
          if (!normalized) {
            throw new Error('Invalid sort section response');
          }
          return normalized;
        },
        invalidatesTags: (_result, _error, { branchCode, menuCode }) =>
          buildMenuInvalidationTags(branchCode, menuCode),
      },
    ),

    createMenuRecipe: builder.mutation<MenuSection, CreateRecipePayload>({
      query: ({ sectionCode, body }) => ({
        url: `/menu/create/recipe/${sectionCode}`,
        method: 'PUT',
        body: recipeRequest(body),
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeSection(normalizePro20Response(response));
        if (!normalized) {
          throw new Error('Invalid create recipe response');
        }
        return normalized;
      },
      invalidatesTags: (_result, _error, { branchCode, menuCode }) =>
        buildMenuInvalidationTags(branchCode, menuCode),
    }),

    updateMenuRecipe: builder.mutation<MenuRecipe, UpdateRecipePayload>({
      query: ({ body }) => ({
        url: `/menu/update/recipe`,
        method: 'PUT',
        body: recipeRequest(body),
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeRecipe(normalizePro20Response(response));
        if (!normalized?.recipeCode) {
          throw new Error('Invalid update recipe response');
        }
        return normalized;
      },
      invalidatesTags: (_result, _error, { branchCode, menuCode }) =>
        buildMenuInvalidationTags(branchCode, menuCode),
    }),

    deleteMenuRecipe: builder.mutation<null, DeleteRecipeParams>({
      query: ({ recipeCode }) => ({
        url: `/menu/delete/recipe/${recipeCode}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { branchCode, menuCode }) =>
        buildMenuInvalidationTags(branchCode, menuCode),
    }),

    uploadMenuLogo: builder.mutation<MenuUploadResponse, MenuUploadParams>({
      query: ({ menuCode, formData }) => ({
        url: `/menu/upload/${menuCode}/logo`,
        method: 'POST',
        body: formData,
      }),
      transformResponse: (response: Pro20ApiResponse<MenuUploadResponse>) =>
        normalizePro20Response(response),
      invalidatesTags: (_result, _error, { branchCode, menuCode }) =>
        buildMenuInvalidationTags(branchCode, menuCode),
    }),

    uploadMenuBackground: builder.mutation<
      MenuUploadResponse,
      MenuUploadParams
    >({
      query: ({ menuCode, formData }) => ({
        url: `/menu/upload/${menuCode}/background`,
        method: 'POST',
        body: formData,
      }),
      transformResponse: (response: Pro20ApiResponse<MenuUploadResponse>) =>
        normalizePro20Response(response),
      invalidatesTags: (_result, _error, { branchCode, menuCode }) =>
        buildMenuInvalidationTags(branchCode, menuCode),
    }),

    removeMenuLogo: builder.mutation<
      null,
      MenuCodeParams & { branchCode?: string }
    >({
      query: ({ menuCode }) => ({
        url: `/menu/remove/${menuCode}/logo`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { branchCode, menuCode }) =>
        buildMenuInvalidationTags(branchCode, menuCode),
    }),

    removeMenuBackground: builder.mutation<
      null,
      MenuCodeParams & { branchCode?: string }
    >({
      query: ({ menuCode }) => ({
        url: `/menu/remove/${menuCode}/background`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { branchCode, menuCode }) =>
        buildMenuInvalidationTags(branchCode, menuCode),
    }),

    uploadRecipeImage: builder.mutation<MenuUploadResponse, RecipeUploadParams>(
      {
        query: ({ recipeCode, formData }) => ({
          url: `/menu/upload/${recipeCode}/image`,
          method: 'POST',
          body: formData,
        }),
        transformResponse: (response: Pro20ApiResponse<MenuUploadResponse>) =>
          normalizePro20Response(response),
        // Modal Save pe create/update + onSuccess list refresh karega.
        invalidatesTags: [],
      },
    ),

    /** Create-recipe flow: no recipeCode yet — generic multimedia upload. */
    uploadMultimedia: builder.mutation<
      MenuUploadResponse,
      { formData: FormData }
    >({
      query: ({ formData }) => ({
        url: '/multimedia/upload',
        method: 'POST',
        body: formData,
      }),
      transformResponse: (response: Pro20ApiResponse<MenuUploadResponse>) =>
        normalizePro20Response(response),
      invalidatesTags: [],
    }),

    removeRecipeImage: builder.mutation<
      null,
      { recipeCode: string; menuCode?: string; branchCode?: string }
    >({
      query: ({ recipeCode }) => ({
        url: `/menu/remove/${recipeCode}/image`,
        method: 'DELETE',
      }),
      // Modal Save pe list refresh — mid-edit GET/card flicker avoid.
      invalidatesTags: [],
    }),

    translateText: builder.mutation<TranslateTextResult, TranslateTextParams>({
      query: ({ text, sourceLanguage = 'IT', targetLanguage }) => ({
        url: '/translationservice/translation/translate',
        method: 'POST',
        body: {
          text,
          source: sourceLanguage,
          target: targetLanguage,
        },
      }),
      transformResponse: (response: unknown) => {
        const unwrapped = normalizePro20Response(response);
        const translatedText =
          readTranslatedText(unwrapped) || readTranslatedText(response);

        if (!translatedText) {
          throw new Error('Empty translation response');
        }

        return { translatedText };
      },
    }),
  }),
});

export const {
  useGetMenusByBranchQuery,
  useGetMenuByCodeQuery,
  useGetMenuIngredientsQuery,
  useGetMenuIngredientPageQuery,
  useGetIngredientCategoriesQuery,
  useSaveIngredientCatalogMutation,
  useCreateMenuMutation,
  useUpdateMenuMutation,
  useDeleteMenuMutation,
  useDuplicateMenuMutation,
  useCreateMenuSectionMutation,
  useUpdateMenuSectionMutation,
  useDeleteMenuSectionMutation,
  useMoveMenuSectionMutation,
  useMoveMenuRecipeMutation,
  useSortSectionRecipesMutation,
  useCreateMenuRecipeMutation,
  useUpdateMenuRecipeMutation,
  useDeleteMenuRecipeMutation,
  useUploadMenuLogoMutation,
  useUploadMenuBackgroundMutation,
  useRemoveMenuLogoMutation,
  useRemoveMenuBackgroundMutation,
  useUploadRecipeImageMutation,
  useUploadMultimediaMutation,
  useRemoveRecipeImageMutation,
  useTranslateTextMutation,
} = menuApi;

/**
 * Clear a recipe photo inside the cached menu without triggering a refetch,
 * so the card and a re-opened modal never show the deleted image.
 */
export function clearCachedRecipePhoto(menuCode: string, recipeCode: string) {
  return menuApi.util.updateQueryData(
    'getMenuByCode',
    { menuCode },
    (draft) => {
      if (!draft) return;
      for (const section of draft.sections) {
        const target = section.recipes.find(
          (item) => item.recipeCode === recipeCode,
        );
        if (target) {
          target.photoUrl = '';
          return;
        }
      }
    },
  );
}
